import { useCallback, useState } from 'react';

import { DASH_META_ROOT, getAuthDashMetaCsrfToken } from '@revelio/auth';
import {
  getHealthCheckId,
  HEALTH_CHECKS,
  type HealthCheckResult,
} from '@revelio/data-access';

interface HealthCheckResponse {
  results: HealthCheckResult[];
  timestamp: string;
}

export interface DailyStats {
  date: string;
  failureCount: number;
  totalChecks: number;
  avgResponseTime: number;
}

export interface HealthSummary {
  totalChecks: number;
  failureCount: number;
  failurePercentage: number;
  avgResponseTime: number;
  currentStatus: 'operational' | 'partial_outage' | 'major_outage';
  dailyStats: DailyStats[];
}

export interface CurrentStatusItem {
  service: string;
  viewGroup: string;
  view: string;
  status: 'success' | 'failure';
  responseTime: number;
  timestamp: string;
  error?: {
    message: string;
    details: unknown;
  };
  isManualCheck: boolean;
}

export interface CurrentStatusResponse {
  items: CurrentStatusItem[];
  timestamp: string;
}

export interface ResponseTimeItem {
  timestamp: string;
  responseTime: number;
  status: 'success' | 'failure';
}

export interface ResponseTimesData {
  service: string;
  viewGroup: string;
  view: string;
  data: ResponseTimeItem[];
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const normalizeHealthCheckResult = (
  result: CurrentStatusItem | HealthCheckResult
): HealthCheckResult => {
  const id =
    'id' in result && result.id
      ? result.id
      : getHealthCheckId(result.service, result.viewGroup, result.view);
  const timestamp = String(result.timestamp);
  const data = 'data' in result && isRecord(result.data) ? result.data : {};

  return {
    id,
    timestamp,
    service: result.service,
    viewGroup: result.viewGroup,
    view: result.view,
    status: result.status,
    responseTime: result.responseTime,
    error: result.error,
    data,
    isManualCheck: result.isManualCheck,
    userEmail: 'userEmail' in result ? result.userEmail : undefined,
  };
};

const toCurrentStatusItem = (result: HealthCheckResult): CurrentStatusItem => ({
  service: result.service,
  viewGroup: result.viewGroup,
  view: result.view,
  status: result.status,
  responseTime: result.responseTime,
  timestamp: result.timestamp,
  error: result.error,
  isManualCheck: result.isManualCheck,
});

const deriveCurrentStatus = (
  results: HealthCheckResult[]
): CurrentStatusItem[] => {
  const latestByCheck = new Map<string, HealthCheckResult>();

  results.forEach((result) => {
    const checkId = getHealthCheckId(
      result.service,
      result.viewGroup,
      result.view
    );
    const existing = latestByCheck.get(checkId);
    if (
      !existing ||
      new Date(result.timestamp) > new Date(existing.timestamp)
    ) {
      latestByCheck.set(checkId, result);
    }
  });

  return Array.from(latestByCheck.values()).map((result) =>
    toCurrentStatusItem(result)
  );
};

export function useHealthCheckApi() {
  const [requests, setRequests] = useState<HealthCheckResult[]>([]);
  const [summary, setSummary] = useState<HealthSummary | null>(null);
  const [currentStatus, setCurrentStatus] = useState<CurrentStatusItem[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isFetchingSummary, setIsFetchingSummary] = useState(false);

  const fetchSummary = useCallback(async () => {
    const csrfToken = getAuthDashMetaCsrfToken();
    if (!csrfToken) {
      console.error('No CSRF token available');
      return;
    }

    setIsFetchingSummary(true);
    try {
      const response = await fetch(`${DASH_META_ROOT}/api/health/summary`, {
        headers: {
          'Content-Type': 'application/json',
          'x-surf-token-dash-meta': csrfToken || '',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: HealthSummary = await response.json();
      setSummary(data);
    } catch (error) {
      console.error('Error fetching health summary:', error);
      setSummary(null);
    } finally {
      setIsFetchingSummary(false);
    }
  }, []);

  const fetchCurrentStatus = useCallback(async () => {
    const csrfToken = getAuthDashMetaCsrfToken();
    if (!csrfToken) {
      console.error('No CSRF token available');
      return;
    }

    setIsFetching(true);
    try {
      const response = await fetch(
        `${DASH_META_ROOT}/api/health/current-status`,
        {
          headers: {
            'Content-Type': 'application/json',
            'x-surf-token-dash-meta': csrfToken || '',
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: CurrentStatusResponse = await response.json();
      setCurrentStatus(data.items);
      setRequests(data.items.map((item) => normalizeHealthCheckResult(item)));
    } catch (error) {
      console.error('Error fetching current status:', error);
      setCurrentStatus([]);
      setRequests([]);
    } finally {
      setIsFetching(false);
    }
  }, []);

  const runHealthChecks = useCallback(async () => {
    const csrfToken = getAuthDashMetaCsrfToken();
    if (!csrfToken) {
      console.error('No CSRF token available');
      return;
    }

    setIsFetching(true);
    try {
      const response = await fetch(`${DASH_META_ROOT}/api/health/check`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-surf-token-dash-meta': csrfToken || '',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: HealthCheckResponse = await response.json();
      setRequests(data.results);
      setCurrentStatus(deriveCurrentStatus(data.results));
      void fetchSummary();
    } catch (error) {
      console.error('Error running health checks:', error);
      setRequests([]);
    } finally {
      setIsFetching(false);
    }
  }, [fetchSummary]);

  const runSingleHealthCheck = useCallback(
    async (checkId: string, variables?: Record<string, unknown>) => {
      const csrfToken = getAuthDashMetaCsrfToken();
      if (!csrfToken) {
        console.error('No CSRF token available');
        return;
      }

      const [service, viewGroup, view] = checkId.split(':');
      const check = HEALTH_CHECKS.find(
        (c) =>
          c.service === service && c.viewGroup === viewGroup && c.view === view
      );

      if (!check) {
        console.error(`No health check found for: ${checkId}`);
        return;
      }

      setIsFetching(true);
      try {
        const response = await fetch(
          `${DASH_META_ROOT}/api/health/check/${checkId}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-surf-token-dash-meta': csrfToken || '',
            },
            body: JSON.stringify({
              variables,
              isManualCheck: true,
            }),
            credentials: 'include',
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = (await response.json()) as HealthCheckResult;
        const normalizedResult = normalizeHealthCheckResult(result);
        // Update current status with the new result
        setCurrentStatus((prev) => {
          const updated = [...prev];
          const index = updated.findIndex(
            (item) =>
              item.service === service &&
              item.viewGroup === viewGroup &&
              item.view === view
          );
          if (index >= 0) {
            updated[index] = toCurrentStatusItem(normalizedResult);
          } else {
            updated.push(toCurrentStatusItem(normalizedResult));
          }
          return updated;
        });
        setRequests((prev) => [normalizedResult, ...prev]);
      } catch (error) {
        console.error(`Error running health check for ${checkId}:`, error);
      } finally {
        setIsFetching(false);
      }
    },
    []
  );

  const fetchServiceHistory = useCallback(
    async (
      service: string,
      options?: {
        startDate?: string;
        endDate?: string;
        limit?: number;
        includeData?: boolean;
      }
    ): Promise<HealthCheckResult[]> => {
      const csrfToken = getAuthDashMetaCsrfToken();
      if (!csrfToken) {
        console.error('No CSRF token available');
        return [];
      }

      try {
        const params = new URLSearchParams({ service });
        if (options?.startDate) params.append('startDate', options.startDate);
        if (options?.endDate) params.append('endDate', options.endDate);
        if (options?.limit) params.append('limit', options.limit.toString());
        if (options?.includeData) {
          params.append('includeData', options.includeData.toString());
        }

        const response = await fetch(
          `${DASH_META_ROOT}/api/health/service?${params.toString()}`,
          {
            headers: {
              'Content-Type': 'application/json',
              'x-surf-token-dash-meta': csrfToken || '',
            },
            credentials: 'include',
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
      } catch (error) {
        console.error(`Error fetching service history for ${service}:`, error);
        return [];
      }
    },
    []
  );

  const fetchResponseTimes = useCallback(
    async (options?: {
      service?: string;
      viewGroup?: string;
      view?: string;
      startDate?: string;
      endDate?: string;
      status?: 'success' | 'failure';
    }): Promise<ResponseTimesData[]> => {
      const csrfToken = getAuthDashMetaCsrfToken();
      if (!csrfToken) {
        console.error('No CSRF token available');
        return [];
      }

      try {
        const params = new URLSearchParams();
        if (options?.service) params.append('service', options.service);
        if (options?.viewGroup) params.append('viewGroup', options.viewGroup);
        if (options?.view) params.append('view', options.view);
        if (options?.startDate) params.append('startDate', options.startDate);
        if (options?.endDate) params.append('endDate', options.endDate);
        if (options?.status) params.append('status', options.status);

        const response = await fetch(
          `${DASH_META_ROOT}/api/health/response-times?${params.toString()}`,
          {
            headers: {
              'Content-Type': 'application/json',
              'x-surf-token-dash-meta': csrfToken || '',
            },
            credentials: 'include',
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
      } catch (error) {
        console.error('Error fetching response times:', error);
        return [];
      }
    },
    []
  );

  const fetchHistory = useCallback(async () => {
    const csrfToken = getAuthDashMetaCsrfToken();
    if (!csrfToken) {
      console.error('No CSRF token available');
      return;
    }

    setIsFetching(true);
    try {
      const response = await fetch(`${DASH_META_ROOT}/api/health/history`, {
        headers: {
          'Content-Type': 'application/json',
          'x-surf-token-dash-meta': csrfToken || '',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: HealthCheckResult[] = await response.json();
      setRequests(data);
      setCurrentStatus(deriveCurrentStatus(data));
    } catch (error) {
      console.error('Error fetching health check history:', error);
      setRequests([]);
    } finally {
      setIsFetching(false);
    }
  }, []);

  const fetchInitialData = useCallback(async () => {
    await Promise.all([fetchSummary(), fetchCurrentStatus()]);
  }, [fetchSummary, fetchCurrentStatus]);

  return {
    requests,
    summary,
    currentStatus,
    isFetching,
    isFetchingSummary,
    runHealthChecks,
    runSingleHealthCheck,
    fetchHistory,
    fetchSummary,
    fetchCurrentStatus,
    fetchServiceHistory,
    fetchResponseTimes,
    fetchInitialData,
  };
}
